import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BUSINESS_PLAN_TYPES } from 'constants/businessPlan';
import { DonutWithTextWrapper } from 'PDF/components/Charts/DonutWithText';
import { IImageChart } from 'PDF/components/Charts/types';
import { PDF_SECTIONS } from 'PDF/constants/sections';
import { prepareSectionId } from 'PDF/helpers/pdfMapping';
import { IPdfPageProps } from 'PDF/types/pdf';
import { useBusinessToolStore } from 'store/useBusinessToolStore';
import { getBusinessPlanChapters, getBusinessPlanType } from 'store/useBusinessToolStore/selectors';

import { getFundingStrategy, getMagazineItems, getProjectCostAndFundingNeeds } from './mapping';

interface IRaisingAndAllocatingFundsProps extends IPdfPageProps {
  isExamplePage: boolean;
}

export const RaisingAndAllocatingFunds = memo(
  ({ saveSectionProps, sectionPicture, isExamplePage }: IRaisingAndAllocatingFundsProps) => {
    const { t } = useTranslation();
    const businessPlanType = useBusinessToolStore(getBusinessPlanType);
    const isInvestor = businessPlanType === BUSINESS_PLAN_TYPES.FOR_INVESTORS;
    const businessPlanChapters = useBusinessToolStore(getBusinessPlanChapters);
    const [fundingStrategyImage, setFundingStrategyImage] = useState<IImageChart[]>([]);
    const [projectCostImage, setProjectCostImage] = useState<IImageChart>();
    const [fundingNeedsImage, setFundingNeedsImage] = useState<IImageChart>();

    const magazineItems = getMagazineItems(businessPlanChapters, businessPlanType, isExamplePage);
    const fundingStrategy = getFundingStrategy(businessPlanChapters, isExamplePage);
    const { donuts: projectCostAndFundingNeedsDonuts, steps: projectCostAndFundingNeedsSteps } =
      getProjectCostAndFundingNeeds(businessPlanChapters, isInvestor, isExamplePage);

    const updateFundingStrategyImage = (title: string, src: string) => {
      setFundingStrategyImage((prevState) => {
        return [...prevState, { title, src }];
      });
    };

    const updateProjectCostAndFundingNeedsImages = (title: string, src: string) => {
      const initDataTitles = projectCostAndFundingNeedsDonuts.map((data) => data.title) ?? [];

      if (title === t('pdfPages.mappings.rasingAndAllocating.projCost') && initDataTitles.includes(title)) {
        setProjectCostImage({ title, src });
      }
      if (title === t('pdfPages.mappings.rasingAndAllocating.fundingNeedsUp') && initDataTitles.includes(title)) {
        setFundingNeedsImage({ title, src });
      }
    };

    useEffect(() => {
      const initDataTitles = projectCostAndFundingNeedsDonuts.map((data) => data.title) ?? [];

      if (!initDataTitles.includes(t('pdfPages.mappings.rasingAndAllocating.projCost'))) {
        setProjectCostImage({ title: '', src: 'emptyChart' });
      }
      if (!initDataTitles.includes(t('pdfPages.mappings.rasingAndAllocating.fundingNeedsUp'))) {
        setFundingNeedsImage({ title: '', src: 'emptyChart' });
      }
    }, []);

    useEffect(() => {
      const hasRequiredImages = !!projectCostImage && !!fundingNeedsImage;
      const isReady = hasRequiredImages && fundingStrategyImage.length > 0;

      if (isReady && sectionPicture) {
        const data = {
          fundingStrategy,
          fundingStrategyImage,
          projectCostAndFundingNeedsImages: [projectCostImage, fundingNeedsImage],
          projectCostAndFundingNeedsSteps,
        };

        saveSectionProps(prepareSectionId(businessPlanType, PDF_SECTIONS.RAISING_AND_ALLOCATING_FUNDS), {
          magazineItems,
          data,
        });
      }
    }, [sectionPicture, projectCostAndFundingNeedsDonuts, projectCostImage, fundingNeedsImage]);

    return (
      <>
        <DonutWithTextWrapper
          aspectRatio={3}
          info={fundingStrategy}
          updateState={updateFundingStrategyImage}
          labelTextWidthDivider={10}
        />
        {projectCostAndFundingNeedsDonuts.map((item, index) => (
          <DonutWithTextWrapper
            key={index}
            aspectRatio={2.5}
            info={item}
            updateState={updateProjectCostAndFundingNeedsImages}
          />
        ))}
      </>
    );
  },
);
